import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { reportsData } from '../data/reports';
import AssessmentTrendChart from '../components/AssessmentTrendChart';

const { 
  FiArrowLeft, FiClock, FiTrendingUp, FiArrowUp, 
  FiArrowDown, FiCalendar, FiChevronRight, FiFileText 
} = FiIcons;

const AssessmentHistory = () => {
  const { title } = useParams();
  const decodedTitle = decodeURIComponent(title);

  const history = useMemo(() => {
    return Object.values(reportsData)
      .filter(r => r.assessmentTitle === decodedTitle)
      .sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate));
  }, [decodedTitle]);

  const latest = history[0];
  const previous = history[1];
  const change = previous ? latest.overallScore - previous.overallScore : 0;

  if (history.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No history found</h2>
          <Link to="/dashboard" className="text-blue-600 hover:underline">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <Link to="/dashboard" className="text-gray-600 hover:text-blue-600 flex items-center mb-4 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Dashboard
          </Link>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{decodedTitle}</h1>
              <p className="text-gray-600">Tracking progress across {history.length} assessment sessions.</p>
            </div>
            <div className="flex gap-4">
              <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4">
                <div className={`p-3 rounded-xl ${change >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                  <SafeIcon icon={change >= 0 ? FiArrowUp : FiArrowDown} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Recent Trend</p>
                  <p className="text-xl font-bold text-gray-900">{change > 0 ? '+' : ''}{change}%</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Trend Visualization */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiTrendingUp} className="mr-2 text-blue-600" /> Longitudinal Growth
              </h3>
              <AssessmentTrendChart history={history} />
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider px-1">Chronological History</h3>
              {history.map((record, idx) => (
                <motion.div 
                  key={record.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition-all group"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                        <SafeIcon icon={FiFileText} className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-bold text-gray-900">{record.completedDate}</p>
                          {idx === 0 && (
                            <span className="px-2 py-0.5 bg-blue-600 text-[10px] font-bold text-white rounded uppercase tracking-widest">Latest</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 italic line-clamp-1">"{record.summary}"</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className={`text-xl font-bold ${record.overallScore >= 80 ? 'text-green-600' : record.overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {record.overallScore}%
                        </p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase">Score</p>
                      </div>
                      <Link 
                        to={`/report/${record.id}`}
                        className="p-3 bg-gray-50 text-gray-400 rounded-xl hover:bg-blue-600 hover:text-white transition-all"
                      >
                        <SafeIcon icon={FiChevronRight} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Comparative Insights */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Historical Comparison</h3>
              <div className="space-y-6">
                {latest.sections.map((section, idx) => {
                  const prevSection = previous?.sections.find(s => s.title === section.title);
                  const secChange = prevSection ? section.score - prevSection.score : 0;
                  
                  return (
                    <div key={idx}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-slate-300">{section.title}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold">{section.score}%</span>
                          {prevSection && (
                            <div className={`flex items-center text-[10px] font-bold ${secChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              <SafeIcon icon={secChange >= 0 ? FiArrowUp : FiArrowDown} className="w-2.5 h-2.5 mr-0.5" />
                              {Math.abs(secChange)}%
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${section.score >= 80 ? 'bg-green-500' : section.score >= 60 ? 'bg-blue-500' : 'bg-red-500'}`}
                          style={{ width: `${section.score}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 pt-6 border-t border-slate-800 text-center">
                <p className="text-xs text-slate-400 mb-4">Want a more detailed breakdown?</p>
                <Link 
                  to="/compare" 
                  className="inline-flex items-center text-sm font-bold text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Open Comparison Tool <SafeIcon icon={FiChevronRight} className="ml-1" />
                </Link>
              </div>
            </div>

            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
              <h4 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
                <SafeIcon icon={FiCalendar} /> Future Planning
              </h4>
              <p className="text-xs text-blue-700 leading-relaxed mb-4">
                Based on your current trajectory, we recommend re-assessing in <strong>April 2024</strong> to track the impact of your operational improvements.
              </p>
              <button className="w-full py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors">
                Schedule Reminder
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentHistory;